import researchResources from "../data/researchResources.json";
import mapResources from "../data/mapResources.json";
import legions from "../data/LegionsTimeline.json";
import { slugifyResourceTitle } from "./LibraryResource";

function libraryUrl(item) {
  const isInternalPdf = item.url?.startsWith("/") && /\.pdf(?:$|\?)/i.test(item.url);
  return isInternalPdf ? `/library/${slugifyResourceTitle(item.title)}` : item.url;
}

function groupByCategory(items) {
  return items.reduce((acc, item) => {
    const category = item.category || "Other";

    if (!acc[category]) {
      acc[category] = [];
    }

    acc[category].push(item);

    return acc;
  }, {});
}

export default function Library() {
  const groupedResearch = groupByCategory(researchResources);
  const groupedMaps = groupByCategory(mapResources);

  const legionGroups = {};

  legions.forEach((item) => {
    const legion = item.legionId || "Other";

    if (!legionGroups[legion]) {
      legionGroups[legion] = [];
    }

    legionGroups[legion].push(item);
  });

  return (
    <div className="min-h-screen bg-stone-950 text-stone-200 px-6 py-12">
      <div className="max-w-6xl mx-auto">

        {/* HEADER */}

        <header className="mb-16">
          <h1 className="text-5xl font-bold mb-6 text-amber-300">
            Roman Britain Library
          </h1>

          <p className="text-lg leading-8 text-stone-300 max-w-4xl">
            The Roman Storyworld Library brings together research papers,
            interactive maps, military timelines, archaeological studies,
            tribal landscapes, Roman roads, frontier systems, and the wider
            historical world that inspired the novels Blood and Honour and
            Shadows of the Eagle.
          </p>

          <p className="text-lg leading-8 text-stone-300 max-w-4xl mt-6">
            This library explores the transformation of Britannia through
            conquest, resistance, trade, belief, engineering, ritual,
            infrastructure, and cultural change from the Iron Age through the
            late Roman period.
          </p>
        </header>

        {/* RESEARCH */}

        <section className="mb-20">
          <h2 className="text-4xl font-bold mb-8 text-amber-200">
            Research Papers & Archaeology
          </h2>

          {Object.entries(groupedResearch).map(([category, items]) => (
            <div key={category} className="mb-12">
              <h3 className="text-2xl font-semibold mb-4 text-amber-100">
                {category}
              </h3>

              <div className="grid md:grid-cols-2 gap-4">
                {items.map((item) => (
                  <a
                    key={item.id}
                    href={libraryUrl(item)}
                    className="block bg-stone-900 border border-stone-800 rounded-xl p-5 hover:border-amber-500 transition"
                  >
                    <h4 className="text-xl font-semibold mb-2 text-amber-300">
                      {item.title}
                    </h4>

                    <p className="text-stone-400 text-sm leading-6">
                      {item.description}
                    </p>

                    <div className="mt-3 flex flex-wrap gap-2">
                      {item.tags?.map((tag) => (
                        <span
                          key={tag}
                          className="text-xs bg-stone-800 px-2 py-1 rounded"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </a>
                ))}
              </div>
            </div>
          ))}
        </section>

        {/* MAPS */}

        <section className="mb-20">
          <h2 className="text-4xl font-bold mb-8 text-amber-200">
            Interactive Maps & Atlases
          </h2>

          {Object.entries(groupedMaps).map(([category, items]) => (
            <div key={category} className="mb-12">
              <h3 className="text-2xl font-semibold mb-4 text-amber-100">
                {category}
              </h3>

              <div className="grid md:grid-cols-2 gap-4">
                {items.map((item) => (
                  <a
                    key={item.id}
                    href={item.url}
                    className="block bg-stone-900 border border-stone-800 rounded-xl p-5 hover:border-amber-500 transition"
                  >
                    <h4 className="text-xl font-semibold mb-2 text-amber-300">
                      {item.title}
                    </h4>

                    <p className="text-stone-400 text-sm leading-6">
                      {item.description}
                    </p>
                  </a>
                ))}
              </div>
            </div>
          ))}
        </section>

        {/* LEGIONS */}

        <section className="mb-20">
          <h2 className="text-4xl font-bold mb-8 text-amber-200">
            Roman Legions & Campaigns
          </h2>

          {Object.entries(legionGroups).map(([legion, items]) => (
            <div key={legion} className="mb-12">
              <h3 className="text-2xl font-semibold mb-4 text-amber-100">
                Legio {legion}
              </h3>

              <div className="space-y-4">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="bg-stone-900 border border-stone-800 rounded-xl p-5"
                  >
                    <h4 className="text-xl text-amber-300 font-semibold">
                      {item.name}
                    </h4>

                    <p className="text-stone-400 mt-2">
                      {item.summary}
                    </p>

                    <p className="text-sm text-stone-500 mt-3">
                      {item.role}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>

        {/* CONQUEST */}

        <section className="mb-20">
          <h2 className="text-4xl font-bold mb-8 text-amber-200">
            Roman Conquest of Britannia
          </h2>

          <div className="bg-stone-900 border border-stone-800 rounded-xl p-6">
            <p className="leading-8 text-stone-300">
              Explore guided resources covering the Roman invasion of Britain,
              campaign routes, battle maps, military operations, Claudius’
              invasion, the conquest of Wales, and the transformation of
              Britannia under Roman rule.
            </p>

            <a
              href="/conquest"
              className="inline-block mt-6 bg-amber-700 hover:bg-amber-600 text-white px-5 py-3 rounded-lg transition"
            >
              Open Conquest Resources
            </a>
          </div>
        </section>

      </div>
    </div>
  );
}
